import { ComponentChildren } from "preact";
import Link from "../Link.tsx";

// Hero Section Components
export const VSHero = ({
  label = "Executive Series",
  title,
  description,
  buttonText = "Watch Series",
  buttonHref = "#series",
  imageSrc,
  imageAlt = "Visual",
}: {
  label?: string;
  title: string;
  description: string;
  buttonText?: string;
  buttonHref?: string;
  imageSrc: string;
  imageAlt?: string;
}) => {
  return (
    <section className="vs-hero">
      <img className="vs-hero-bg" src={imageSrc} alt={imageAlt} />
      <div className="vs-hero-inner">
        <div className="vs-hero-grid">
          <div className="vs-hero-content">
            {label && <span className="vs-hero-label">{label}</span>}
            <h1 className="vs-hero-title">{title}</h1>
            <p className="vs-hero-desc">{description}</p>
            <div className="vs-hero-cta">
              <Link href={buttonHref} className="cta-button">
                {buttonText}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Featured Video Component
export const VSFeaturedVideo = ({
  partLabel,
  title,
  duration,
  youtubeId,
}: {
  partLabel: string;
  title: string;
  duration?: string;
  youtubeId: string;
}) => {
  const embedSrc = `https://www.youtube-nocookie.com/embed/${youtubeId}?rel=0&modestbranding=1&playsinline=1`;

  return (
    <section className="vs-featured-section">
      <div className="vs-featured-container">
        <div className="vs-featured-header">
          <h2 className="vs-featured-title" id="vs-featured-title">
            {partLabel}: {title}
          </h2>
          {duration && <span className="vs-featured-duration" id="vs-featured-duration">{duration}</span>}
        </div>
        <div className="vs-featured-video-wrapper">
          <iframe
            id="vs-featured-iframe"
            src={embedSrc}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            loading="lazy"
          ></iframe>
        </div>
      </div>
    </section>
  );
};

// Series Overview Sidebar Component
export const VSOverview = ({
  title = "About the Series",
  description,
  topics,
  footerText,
  footerText2,
  children,
}: {
  title?: string;
  description: string;
  topics: string[];
  footerText?: string;
  footerText2?: string;
  children: ComponentChildren;
}) => {
  return (
    <section className="vs-series-section" id="series">
      <div className="vs-series-grid">
        <div className="vs-series-sidebar">
          <h3 className="vs-sidebar-title">{title}</h3>
          <p className="vs-sidebar-desc">{description}</p>
          {topics && topics.length > 0 && (
            <div className="vs-sidebar-topics">
              <h4>Key Topics</h4>
              <ul>
                {topics.map((topic, i) => (
                  <li key={i}>
                    <svg style={{ flexShrink: '0', color: '#c16b29' }} width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="icon">
                      <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z" fill="currentColor" />
                    </svg>
                    <span>{topic}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {footerText && <p className="vs-sidebar-footer">{footerText}</p>}
          {footerText2 && <p className="vs-sidebar-footer second">{footerText2}</p>}
        </div>
        <div>{children}</div>
      </div>
    </section>
  );
};

// Video Grid Component
export const VSVideoGrid = ({ children }: { children: ComponentChildren }) => {
  return (
    <>
      <div className="vs-video-grid-container">{children}</div>
      <script
        dangerouslySetInnerHTML={{
          __html: `
            document.addEventListener('DOMContentLoaded', () => {
              const cards = document.querySelectorAll('.vs-video-card[data-youtube-id]');
              cards.forEach(card => {
                card.addEventListener('click', (e) => {
                  e.preventDefault();
                  const youtubeId = card.getAttribute('data-youtube-id');
                  const part = card.getAttribute('data-part');
                  const title = card.getAttribute('data-title');
                  const duration = card.getAttribute('data-duration');

                  const iframe = document.getElementById('vs-featured-iframe');
                  if (iframe) {
                    iframe.src = 'https://www.youtube-nocookie.com/embed/' + youtubeId + '?rel=0&modestbranding=1&playsinline=1&autoplay=1';
                    iframe.title = title;
                    iframe.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }
                  
                  const titleEl = document.getElementById('vs-featured-title');
                  if (titleEl) {
                    titleEl.innerText = part + ': ' + title;
                  }

                  const durationEl = document.getElementById('vs-featured-duration');
                  if (durationEl && duration) {
                    durationEl.innerText = duration;
                  }
                });
              });
            });
          `,
        }}
      />
    </>
  );
};

// Video Card Component
export const VSVideoCard = ({
  part,
  title,
  duration,
  youtubeId,
  isComingSoon = false,
}: {
  part: string;
  title: string;
  duration?: string;
  youtubeId?: string;
  isComingSoon?: boolean;
}) => {
  if (isComingSoon) {
    return (
      <div className="vs-video-card coming-soon">
        <div className="vs-video-card-thumb">
          <div className="vs-video-card-coming">
            <span className="material-symbols-outlined icon">lock</span>
            <span className="text">Coming Soon</span>
          </div>
        </div>
        <div className="vs-video-card-content">
          <span className="vs-video-card-part">{part}</span>
          <h4 className="vs-video-card-title">{title}</h4>
        </div>
      </div>
    );
  }

  // To make the whole card clickable, we'll wrap it in an anchor linking to the youtube video.
  // Alternatively we could have a modal, but standard link to youtube or same page works for now.
  const videoUrl = youtubeId ? `https://www.youtube.com/watch?v=${youtubeId}` : "#";

  return (
    <a
      href={videoUrl}
      target={youtubeId ? "_blank" : "_self"}
      rel={youtubeId ? "noopener noreferrer" : ""}
      className="vs-video-card"
      data-youtube-id={youtubeId}
      data-part={part}
      data-title={title}
      data-duration={duration}
    >
      <div className="vs-video-card-thumb">
        {youtubeId ? (
          <img
            src={`https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`}
            alt={title}
          />
        ) : (
          <div style={{ width: "100%", height: "100%", backgroundColor: "var(--vs-surface-variant)" }} />
        )}
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="play-icon">
          <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM10 16.5V7.5L16 12L10 16.5Z" fill="currentColor" />
        </svg>
        {duration && <div className="vs-video-card-duration">{duration}</div>}
      </div>
      <div className="vs-video-card-content">
        <span className="vs-video-card-part">{part}</span>
        <h4 className="vs-video-card-title">{title}</h4>
      </div>
    </a>
  );
};
